import ResizeObserver from 'resize-observer-polyfill';
import {VNode} from 'snabbdom/vnode';
import {AttrsSchema, Component, jsx} from 'panel';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {LabelTheme, Section} from 'common/components/items-menu/types';
import {ChartLegendEl, ToggleCheckboxDetail} from 'common/components/chart-legend';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {Placement} from 'common/components/tooltip/types';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {chromeHeaderHeight} from 'common/widgets/chrome/chrome-header/constants';

import {SegmentationLimitInfoEl} from '../segmentation-limit-info';

interface SegmentFilterState {
  selfHeight: string;
}

interface SegmentFilterAttrsSchema {
  'is-sticky': boolean;
}

interface LegendSectionItemValue {
  sectionIdx: number;
  segmentId: string;
}

export class SegmentFilter extends Component<
  SegmentFilterState,
  SegmentFilterAttrsSchema,
  any,
  any,
  ChartsContextRegistry
> {
  public static get attrsSchema(): AttrsSchema<SegmentFilterAttrsSchema> {
    return {
      'is-sticky': {type: `boolean`},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {
        selfHeight: `0px`,
      },
      contexts: [`segmentFilterController` as const],
    };
  }

  private selfResizeObserver: ResizeObserver;
  private windowScrollListener: EventListener;

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  connectedCallback() {
    if (this.attr(`is-sticky`)) {
      this.windowScrollListener = () => this.updateStickyHeight();
      window.addEventListener(`scroll`, this.windowScrollListener);

      this.updateStickyHeight();
    } else {
      this.selfResizeObserver = new ResizeObserver((entries) => {
        const {height} = entries[0].contentRect;
        this.update({
          selfHeight: `${height}px`,
        });
      });

      this.selfResizeObserver.observe(this);
    }

    super.connectedCallback();
  }

  /**
   * When sticky, this makes sure to vertically fill the visible portion of the parent element
   * and calculate the remaining distance to the bottom of the page when reaching the end of the parent element.
   */
  updateStickyHeight() {
    if (this.parentElement) {
      const parentBounds = this.parentElement.getBoundingClientRect();

      let offset = chromeHeaderHeight;
      if (parentBounds.top <= 0 && parentBounds.height > window.innerHeight) {
        const distToBottom = Math.abs(Math.max(0, window.innerHeight - parentBounds.top - parentBounds.height));
        offset += distToBottom;
      }

      this.update({selfHeight: `calc(100vh - ${offset}px)`});
    }
  }

  disconnectedCallback() {
    this.selfResizeObserver?.disconnect();
    window.removeEventListener(`scroll`, this.windowScrollListener);
    super.disconnectedCallback();
  }

  private renderTemplate(): VNode {
    const legendSections: Array<Section<LegendSectionItemValue>> = this.segmentFilterController
      .getSections()
      .map((section, sectionIdx) => {
        const numSelectedLabel =
          section.apiResultsTruncated && !section.suppressShowingTop
            ? `${this.segmentFilterController.getSelectedCount(sectionIdx)} of top ${section.items.length}`
            : `${this.segmentFilterController.getSelectedCount(sectionIdx)} of ${section.items.length}`;
        const sectionLabel = section.label ? `${section.label} ` : ``;

        const sectionLabelIconRenderer = section.apiResultsTruncated
          ? () => {
              return (
                <SegmentationLimitInfoEl
                  attrs={{
                    'segmentation-limit': this.segmentFilterController.segmentationQueryLimit,
                    'export-segmentation-limit': this.segmentFilterController.exportSegmentationQueryLimit,
                  }}
                />
                // expecting 'export-csv' event to bubble from SegmentationLimitInfo, handled by an ancestor
              );
            }
          : null;

        return {
          label: `${sectionLabel}${numSelectedLabel}`,
          showLimit: 12,
          items: section.items.map((segmentItem) => ({
            label: segmentItem.label,
            value: {segmentId: segmentItem.id, sectionIdx},
            isSelected: this.segmentFilterController.isItemSelected(sectionIdx, segmentItem.id),
            color: this.segmentFilterController.getItemColor(sectionIdx, segmentItem.id),
            labelTheme: LabelTheme.Soft,
            wrapLabel: true,
          })),
          sectionLabelIconRenderer,
        };
      });

    const moreThanLimit = legendSections[0]?.items.length > this.segmentFilterController.selectAllLimit;

    const isSticky = this.attr(`is-sticky`);
    return (
      <div sel='.root' hook={isSticky ? stickyElemHooks : {}} style={{position: isSticky ? `sticky` : `static`}}>
        <ChartLegendEl
          sel='.large-legend'
          attrs={{
            loading: !this.segmentFilterController.hasLoaded(),
            height: this.state.selfHeight,
            theme: ChartLegendTheme.Primary,
            'show-select-all': true,
            'select-all-tooltip': moreThanLimit
              ? {
                  text: `Cannot select more than ${this.segmentFilterController.selectAllLimit} segments at once`,
                  placement: Placement.Left,
                }
              : undefined,
            'enable-search': true,
            'max-legend-items': 12,
          }}
          props={{
            sections: legendSections,
          }}
          on={{
            togglecheckbox: (ev: CustomEvent<ToggleCheckboxDetail<LegendSectionItemValue>>) => {
              if (ev.detail.isSelectAll) {
                this.segmentFilterController.toggleAllItems();
              } else if (ev.detail.item) {
                const {sectionIdx, segmentId} = ev.detail.item.value;
                this.segmentFilterController.toggleItem(sectionIdx, segmentId);
              }
            },
          }}
        />
      </div>
    );
  }
}

export const SegmentFilterEl = defineAndMakeJsxFactory(`mp-segment-filter`, SegmentFilter);
